/**
 * Convoys (ROUND4 §1): the vehicles that cross the bridge, steam out of the yard, drive off the laager,
 * come down the road in a window, carry the courier and lead the column over the charges.
 *
 * A vehicle is a machine enemy with a `route`. It drives that run in a straight line from waypoint to
 * waypoint, crushing wire and sandbags and stopping at anything solid, so a route belongs on a ROAD - which
 * is what `roadRun` gives: points sampled down the map's own road between two z values. The last waypoint is
 * where it leaves the map, so a run should end where it can be driven to.
 */

import { atan2 } from '../../sim/math';
import { VEHICLES } from '../../sim/tuning';
import type { EnemyKind, Polyline, SpawnDef } from '../../sim/types';
import { FLAG } from '../grid';
import type { Point } from '../types';
import type { GoalCtx } from './types';

/** How far apart route waypoints are laid: close enough that a bend in the road is still a bend. */
const STEP = 7;

/** The lane a vehicle needs kept clear either side of its run. */
const LANE_HALF = 2.2;

/**
 * Points down a road polyline between two z values, every `STEP` metres, ordered from `fromZ` to `toZ`.
 * The road is sampled a metre at a time, so a bend is followed rather than cut.
 */
export function roadRun(road: ReadonlyArray<Point>, fromZ: number, toZ: number): Point[] {
  const lo = Math.min(fromZ, toZ);
  const hi = Math.max(fromZ, toZ);
  const sampled: Point[] = [];
  for (let i = 1; i < road.length; i++) {
    const a = road[i - 1];
    const b = road[i];
    if (!a || !b) continue;
    const steps = Math.max(1, Math.ceil(Math.sqrt((b.x - a.x) * (b.x - a.x) + (b.z - a.z) * (b.z - a.z))));
    for (let s = 0; s <= steps; s++) {
      const p = { x: a.x + ((b.x - a.x) * s) / steps, z: a.z + ((b.z - a.z) * s) / steps };
      if (p.z >= lo && p.z <= hi) sampled.push(p);
    }
  }
  sampled.sort((p, q) => (fromZ <= toZ ? p.z - q.z : q.z - p.z));
  const out: Point[] = [];
  for (const p of sampled) {
    const last = out[out.length - 1];
    if (!last || Math.abs(p.z - last.z) >= STEP) out.push(p);
  }
  const end = sampled[sampled.length - 1];
  const tail = out[out.length - 1];
  if (end && (!tail || Math.abs(end.z - tail.z) > 0.5)) out.push(end);
  return out;
}

export interface VehicleOptions {
  kind: EnemyKind;
  /** The run, in order. It starts at the first point and leaves the map at the last. */
  route: ReadonlyArray<Point>;
  speed?: number;
  loop?: boolean;
  startAtSeconds?: number;
  /** Dormant until the timed event or alert consequence tagged this; absent = on the map from the start. */
  wakeTag?: string;
  carries?: number;
  carriesKind?: EnemyKind;
  carriesTarget?: boolean;
  dismountAt?: Point;
  /** This vehicle IS that parked prop, under way: the prop goes when it does (`SpawnDef.replacesPropId`). */
  replacesPropId?: number;
  /** Metres to hold back along the run's first leg: a file of lorries nose to tail. */
  back?: number;
  /** Where it stands before it pulls away, if that is not the first waypoint (the prop it replaces). */
  from?: Point;
}

/** Add one vehicle to the map. Returns its spawn, or null when the run has no road in it. */
export function vehicle(g: GoalCtx, o: VehicleOptions): SpawnDef | null {
  const first = o.route[0];
  const second = o.route[1];
  if (!first || !second) {
    g.map.problems.push(`${g.ctx.spec.id}: no road for the ${o.kind} to drive`);
    return null;
  }
  const facing = atan2(second.x - first.x, second.z - first.z);
  const back = o.back ?? 0;
  const len = Math.sqrt((second.x - first.x) ** 2 + (second.z - first.z) ** 2) || 1;
  const start = o.from ?? {
    x: first.x - ((second.x - first.x) / len) * back,
    z: first.z - ((second.z - first.z) / len) * back,
  };
  const points: Polyline = { points: o.route.map((p): [number, number] => [p.x, p.z]), width: 4.2 };
  // Nothing is built or scattered into the lane afterwards: a tree in the road stops a lorry dead.
  g.map.mask.line(points.points, LANE_HALF, FLAG.keep);
  return g.map.spawn({
    kind: o.kind,
    x: Math.round(start.x * 2) / 2,
    z: Math.round(start.z * 2) / 2,
    facing,
    route: {
      points,
      speed: o.speed ?? VEHICLES.speed,
      loop: o.loop === true,
      ...(o.startAtSeconds === undefined ? {} : { startAtSeconds: o.startAtSeconds }),
    },
    ...(o.wakeTag === undefined ? {} : { dormantUntil: 'timedEvent' as const, wakeTag: o.wakeTag }),
    ...(o.carries === undefined ? {} : { carries: o.carries }),
    ...(o.carriesKind === undefined ? {} : { carriesKind: o.carriesKind }),
    ...(o.carriesTarget === true ? { carriesTarget: true } : {}),
    ...(o.dismountAt === undefined ? {} : { dismountAt: { x: o.dismountAt.x, z: o.dismountAt.z } }),
    ...(o.replacesPropId === undefined ? {} : { replacesPropId: o.replacesPropId }),
  });
}

/** The vehicles of a mission, as the asserts read them. */
export const vehiclesOf = (spawns: ReadonlyArray<SpawnDef>): SpawnDef[] => spawns.filter((s) => s.route !== undefined);

/** Does this run pass within `reach` metres of a point - the span it must cross, a culvert it must roll over? */
export function runPasses(s: SpawnDef, x: number, z: number, reach: number): boolean {
  const pts = s.route?.points.points ?? [];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    if (!a || !b) continue;
    const vx = b[0] - a[0];
    const vz = b[1] - a[1];
    const len2 = vx * vx + vz * vz;
    const t = len2 === 0 ? 0 : Math.min(1, Math.max(0, ((x - a[0]) * vx + (z - a[1]) * vz) / len2));
    const dx = x - (a[0] + vx * t);
    const dz = z - (a[1] + vz * t);
    if (dx * dx + dz * dz <= reach * reach) return true;
  }
  return false;
}
